import Phaser from 'phaser';
import { CampManager, type Survivor } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { GAME_W, GAME_H, GAME_CX } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import {
  IslandTileRenderer,
  TILE_SIZE,
  type IslandTileType,
  type IslandBuildingId,
  type IslandInteractableId,
  type IslandDecorationId,
  type IslandSurvivorClass,
} from '../rendering/IslandTileRenderer';
import { addHoverPulse } from '../ui/ButtonPolish';
import { showBugReportOverlay } from '../ui/BugReportOverlay';
import { captureCanvas } from '../systems/BugReportContext';
import { pickDialogue } from '../data/survivorDialogues';
import { ensureParticleTexture } from '../rendering/ParticleEffects';

// === SCÈNE CAMP — VUE ÎLE PIXEL ART (Sprints A1 + A2 + B1 + A3) ===
// Refonte du camp : vue top-down d'une île tile-based, capitaine déplaçable
// au point & click. Activée via feature flag (voir IslandCampFlag).
//
// Sprint A1 : île + capitaine + déplacement point & click.
// Sprint A2 : 6 bâtiments cliquables (Atelier, Coffre, Dortoir, Vigie, Taverne,
//             Boutique).
// Sprint B1 : zone départ raid (portail jungle), Chantier Naval verrouillé (teaser
//             acte 2), menus diégétiques (panneau, table de navigation, feu de camp),
//             palmiers et décorations.
// Sprint A3 : survivants vivants animés sur l'île — patrouilles déterministes
//             autour d'une zone d'ancrage, dialogues contextuels au clic
//             (rotation quotidienne).

const HEADER_H = 50;

/** Vitesse de déplacement du capitaine en pixels/seconde */
const CAPTAIN_SPEED = 90;

/** Vitesse des survivants en patrouille (plus lente que le capitaine). */
const SURVIVOR_SPEED = 35;

/** Distance (px) en-dessous de laquelle on considère que le capitaine est "arrivé". */
const ARRIVAL_THRESHOLD = TILE_SIZE * 1.2;

/** Rayon de patrouille d'un survivant autour de sa zone d'ancrage (en tiles). */
const PATROL_RADIUS = 3;

/** Pause minimale / maximale (ms) entre deux déplacements de patrouille. */
const PATROL_PAUSE_MIN = 2800;
const PATROL_PAUSE_MAX = 6500;

/** Durée d'affichage d'une bulle de dialogue (ms). */
const BUBBLE_DURATION = 3200;

/** Cooldown entre deux clics sur le même survivant (anti-spam). */
const BUBBLE_COOLDOWN = 600;

/** Type de tile pour la grille interne */
type Tile = IslandTileType;

const WALKABLE_TILES: ReadonlySet<Tile> = new Set<Tile>(['sand', 'grass', 'path']);

// === DESCRIPTION DES BÂTIMENTS POSÉS SUR L'ÎLE ===

/** Type d'action lorsque le joueur clique sur un bâtiment construit. */
type BuildingAction =
  | { kind: 'scene'; sceneKey: string }
  | { kind: 'tooltip'; message: string }
  | { kind: 'locked'; message: string };

/** Définition d'un bâtiment placé sur l'île. */
interface IslandBuilding {
  id: IslandBuildingId;
  /** Label court affiché au-dessus du sprite. */
  label: string;
  /** Coordonnées normalisées [0..1] dans la grille (placement déterministe). */
  anchor: { x: number; y: number };
  /** Action déclenchée à l'arrivée du capitaine quand le bâtiment est construit. */
  action: BuildingAction;
  /** Comment évaluer l'état construit/non. `always` = pas dans le système building,
   *  `locked` = teaser Acte 2 (chantier naval) toujours verrouillé. */
  builtCheck:
    | { kind: 'building'; buildingId: 'workshop' | 'storage' | 'dormitory' | 'watchtower' | 'tavern' }
    | { kind: 'always' }
    | { kind: 'locked' };
  /** Si true, force la tile sous le bâtiment en sable (pour les bâtiments de plage). */
  forceSand?: boolean;
}

/** Positions thématiques :
 *  - Vigie au nord (en hauteur)
 *  - Dortoir au nord-ouest, Atelier au nord-est (cœur de l'île)
 *  - Taverne au centre (festif, près du sentier)
 *  - Coffre au sud-ouest, Boutique au sud-est (près de la plage)
 *  - Chantier Naval à l'ouest sur la plage (teaser Acte 2, toujours verrouillé)
 */
const BUILDINGS: IslandBuilding[] = [
  {
    id: 'watchtower',
    label: '🗼 Vigie',
    anchor: { x: 0.5, y: 0.18 },
    action: { kind: 'scene', sceneKey: 'VigieScene' },
    builtCheck: { kind: 'building', buildingId: 'watchtower' },
  },
  {
    id: 'dormitory',
    label: '🛏️ Dortoir',
    anchor: { x: 0.22, y: 0.35 },
    action: { kind: 'scene', sceneKey: 'DormScene' },
    builtCheck: { kind: 'building', buildingId: 'dormitory' },
  },
  {
    id: 'workshop',
    label: '🔨 Atelier',
    anchor: { x: 0.78, y: 0.35 },
    action: { kind: 'scene', sceneKey: 'AtelierScene' },
    builtCheck: { kind: 'building', buildingId: 'workshop' },
  },
  {
    id: 'tavern',
    label: '🍺 Taverne',
    anchor: { x: 0.68, y: 0.58 },
    action: { kind: 'scene', sceneKey: 'TavernScene' },
    builtCheck: { kind: 'building', buildingId: 'tavern' },
  },
  {
    id: 'storage',
    label: '📦 Coffre',
    anchor: { x: 0.28, y: 0.76 },
    action: { kind: 'scene', sceneKey: 'StorageScene' },
    builtCheck: { kind: 'building', buildingId: 'storage' },
  },
  {
    id: 'shop',
    label: '🛒 Boutique',
    anchor: { x: 0.72, y: 0.76 },
    action: {
      kind: 'tooltip',
      message: 'Le marchand n\'est présent qu\'en expédition.',
    },
    builtCheck: { kind: 'always' },
  },
  // Sprint B1 — Chantier Naval (teaser Acte 2, toujours verrouillé)
  {
    id: 'shipyard',
    label: '🔒 Chantier Naval',
    anchor: { x: 0.08, y: 0.58 },
    action: {
      kind: 'locked',
      message: '🔒 Chantier Naval\nBientôt disponible (Acte 2)',
    },
    builtCheck: { kind: 'locked' },
    forceSand: true,
  },
];

// === OBJETS INTERACTIFS DIÉGÉTIQUES (Sprint B1) ===

/** Type d'action déclenchée par un objet diégétique. */
type InteractableAction =
  | { kind: 'scene'; sceneKey: string; sceneData?: Record<string, unknown> }
  | { kind: 'raid' }
  | { kind: 'signboard_menu' }
  | { kind: 'stats' }
  | { kind: 'settings' };

interface IslandInteractable {
  id: IslandInteractableId;
  label: string;
  anchor: { x: number; y: number };
  action: InteractableAction;
}

/** Objets diégétiques posés sur l'île :
 *  - Portail jungle (zone départ raid) au sud-est
 *  - Panneau d'affichage près du centre (menus communauté)
 *  - Table de navigation près de la plage sud (settings)
 *  - Feu de camp au centre (stats joueur)
 */
const INTERACTABLES: IslandInteractable[] = [
  {
    id: 'raid_portal',
    label: '⚔️ Partir en raid',
    anchor: { x: 0.92, y: 0.85 },
    action: { kind: 'raid' },
  },
  {
    id: 'signboard',
    label: '📋 Tableau d\'affichage',
    anchor: { x: 0.43, y: 0.45 },
    action: { kind: 'signboard_menu' },
  },
  {
    id: 'nav_table',
    label: '🧭 Navigation',
    anchor: { x: 0.58, y: 0.85 },
    action: { kind: 'settings' },
  },
  {
    id: 'campfire',
    label: '🔥 Feu de camp',
    anchor: { x: 0.5, y: 0.52 },
    action: { kind: 'stats' },
  },
];

// === DÉCORATIONS NON INTERACTIVES (Sprint B1) ===

interface IslandDecoration {
  id: IslandDecorationId;
  /** Coordonnées normalisées [0..1]. Pas de marche ni d'interaction. */
  anchor: { x: number; y: number };
}

/** Positions fixes — donnent vie à l'île sans encombrer les chemins. */
const DECORATIONS: IslandDecoration[] = [
  // Palmiers sur la bordure de plage
  { id: 'palm_tree', anchor: { x: 0.15, y: 0.22 } },
  { id: 'palm_tree', anchor: { x: 0.85, y: 0.22 } },
  { id: 'palm_tree', anchor: { x: 0.12, y: 0.5 } },
  { id: 'palm_tree', anchor: { x: 0.88, y: 0.5 } },
  { id: 'palm_tree', anchor: { x: 0.16, y: 0.88 } },
  { id: 'palm_tree', anchor: { x: 0.84, y: 0.92 } },
  // Tonneaux et caisses près des bâtiments / du port
  { id: 'barrel', anchor: { x: 0.18, y: 0.66 } },
  { id: 'barrel', anchor: { x: 0.82, y: 0.66 } },
  { id: 'crate', anchor: { x: 0.36, y: 0.65 } },
  { id: 'crate', anchor: { x: 0.62, y: 0.7 } },
  // Filets de pêche sur le sable
  { id: 'fishing_net', anchor: { x: 0.4, y: 0.95 } },
  { id: 'fishing_net', anchor: { x: 0.74, y: 0.95 } },
  // Petits rochers décoratifs
  { id: 'small_rock', anchor: { x: 0.34, y: 0.25 } },
  { id: 'small_rock', anchor: { x: 0.66, y: 0.27 } },
  { id: 'small_rock', anchor: { x: 0.55, y: 0.7 } },
];

/** État runtime d'un bâtiment placé (sprite + label + position grille). */
interface PlacedBuilding {
  def: IslandBuilding;
  sprite: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  built: boolean;
  /** True si le bâtiment est verrouillé (chantier naval) — état permanent. */
  locked: boolean;
  /** Tile occupée par la base du bâtiment (pour ne pas y marcher). */
  tile: { x: number; y: number };
  /** Tile d'approche (devant le bâtiment, marchable). */
  approachTile: { x: number; y: number };
}

/** État runtime d'un objet interactif (Sprint B1). */
interface PlacedInteractable {
  def: IslandInteractable;
  sprite: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  tile: { x: number; y: number };
  approachTile: { x: number; y: number };
}

// === SURVIVANTS ANIMÉS (Sprint A3) ===

/** Zones d'ancrage normalisées [0..1] où patrouillent les survivants.
 *  Distribuées autour des bâtiments de vie : taverne, dortoir, feu de camp.
 *  Les zones sont assignées round-robin selon l'index du survivant vivant. */
const PATROL_ZONES: ReadonlyArray<{ x: number; y: number }> = [
  { x: 0.65, y: 0.55 }, // près de la taverne (festif)
  { x: 0.25, y: 0.40 }, // devant le dortoir
  { x: 0.5,  y: 0.65 }, // près du feu de camp
  { x: 0.38, y: 0.50 }, // au centre, sur les sentiers
  { x: 0.6,  y: 0.40 }, // entre taverne et atelier
];

/** Mapping classe CampManager → classe sprite (identique aujourd'hui mais isolé
 *  pour le jour où on ajoutera des classes côté gameplay sans sprite). */
function classToSpriteClass(c: 'warrior' | 'ranger' | 'medic'): IslandSurvivorClass {
  return c;
}

/** État runtime d'un survivant animé sur l'île. */
interface PlacedSurvivor {
  data: Survivor;
  spriteClass: IslandSurvivorClass;
  sprite: Phaser.GameObjects.Image;
  /** Tile d'ancrage (centre de la zone de patrouille). */
  anchorTile: { x: number; y: number };
  /** Tween de déplacement courant (null si idle). */
  moveTween: Phaser.Tweens.Tween | null;
  /** Anim 2-frames pendant le mouvement. */
  animEvent: Phaser.Time.TimerEvent | null;
  /** Timer pour la prochaine étape de patrouille. */
  nextStepEvent: Phaser.Time.TimerEvent | null;
  /** Compteur de pas (utilisé pour le hash de patrouille déterministe). */
  step: number;
  /** Bulle de dialogue courante (1 par survivant). */
  bubble: Phaser.GameObjects.Container | null;
  /** Timestamp du dernier clic ouvert (anti-spam). */
  lastBubbleAt: number;
  /** Indicateur d'étoiles selon le niveau du survivant (Sprint A4 post-merge polish). */
  levelBadge: Phaser.GameObjects.Text | null;
}

export class IslandCampScene extends Phaser.Scene {
  private camp!: CampManager;
  private meta!: MetaManager;

  // Grille de l'île
  private grid: Tile[][] = [];
  private cols = 0;
  private rows = 0;
  private gridOriginX = 0;
  private gridOriginY = 0;

  // Capitaine
  private captain!: Phaser.GameObjects.Image;
  private captainMoveTween: Phaser.Tweens.Tween | null = null;
  private captainAnimEvent: Phaser.Time.TimerEvent | null = null;

  // Bâtiments (Sprint A2 + B1 shipyard)
  private buildings: PlacedBuilding[] = [];
  // Objets interactifs diégétiques (Sprint B1)
  private interactables: PlacedInteractable[] = [];
  // Survivants animés (Sprint A3)
  private survivors: PlacedSurvivor[] = [];
  // Sprint A4 — images water à toggler pour animer les vagues
  private waterImages: Phaser.GameObjects.Image[] = [];
  private waterToggleEvent: Phaser.Time.TimerEvent | null = null;
  private waterFrameAlt = false;
  // Sprint A4 — mouettes en orbite + battement d'ailes
  private seagulls: Phaser.GameObjects.Image[] = [];
  private seagullFrameEvent: Phaser.Time.TimerEvent | null = null;
  private seagullFrameDown = false;
  // Sprint A4 — émitters du feu de camp (fumée + étincelles)
  private campfireEmitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  // Sprint A4 — overlay teinté pour le cycle jour/nuit (basé sur l'heure réelle)
  private timeOfDayOverlay: Phaser.GameObjects.Rectangle | null = null;
  /** Timestamp de départ pour l'animation d'orbite (basé sur this.time.now). */
  private islandStartTime = 0;

  /** Action en attente à exécuter dès que le capitaine arrive devant un objet. */
  private pendingArrival:
    | { kind: 'building'; building: PlacedBuilding }
    | { kind: 'interactable'; interactable: PlacedInteractable }
    | null = null;

  constructor() {
    super({ key: 'IslandCampScene' });
  }

  init(data: { camp?: CampManager; meta?: MetaManager }): void {
    this.camp = data?.camp ?? new CampManager();
    this.meta = data?.meta ?? new MetaManager();
  }

  async create(): Promise<void> {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    // Fond mer profond (visible aux bords si l'île ne couvre pas tout)
    this.add.rectangle(GAME_CX, GAME_H / 2, GAME_W, GAME_H, 0x0e2040).setDepth(-10);

    // Génération des textures pixel art (idempotent — skip si déjà chargées)
    const tileRenderer = new IslandTileRenderer(this);
    try {
      await tileRenderer.generateAllTextures();
    } catch (e) {
      console.warn('[IslandCampScene] Erreur génération tuiles:', e);
    }

    this.islandStartTime = this.time.now;

    this.drawHeader();
    this.computeGridDimensions();
    this.generateIsland();
    this.placeBuildings();
    this.placeInteractables();
    this.drawTiles();
    this.drawDecorations();
    this.drawBuildings();
    this.drawInteractables();
    this.spawnCaptain();
    this.spawnSurvivors();
    this.spawnSeagulls();
    this.applyTimeOfDayOverlay();
    this.installInput();
  }

  // ============================================================
  // HEADER
  // ============================================================

  private drawHeader(): void {
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a1428).setDepth(10);
    this.add.text(GAME_CX, 14, '🏴‍☠️ ÎLE DU CAPITAINE', {
      fontSize: '16px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);
    this.add.text(GAME_CX, 34, 'Clique sur le sol pour te déplacer.', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Sprint B1 — les boutons UI (Menu, Ancien camp) sont retirés au profit
    // d'objets diégétiques sur l'île (panneau d'affichage). Le panneau ouvre
    // un menu avec "Retour menu principal" et "Ancien camp" (escape hatch).
  }

  // ============================================================
  // GÉNÉRATION DE L'ÎLE
  // ============================================================

  /**
   * Calcule la grille en fonction des dimensions canvas.
   * Le tile size reste 32px ; le nombre de cols/rows dépend de l'écran.
   */
  private computeGridDimensions(): void {
    const playableH = GAME_H - HEADER_H;
    this.cols = Math.floor(GAME_W / TILE_SIZE);
    this.rows = Math.floor(playableH / TILE_SIZE);
    // Centre la grille s'il reste des pixels non couverts (rare avec 32px)
    this.gridOriginX = Math.floor((GAME_W - this.cols * TILE_SIZE) / 2);
    this.gridOriginY = HEADER_H + Math.floor((playableH - this.rows * TILE_SIZE) / 2);
  }

  /**
   * Génère une île organique : ellipse centrée + perturbation déterministe
   * (somme de sinusoïdes en fonction de l'angle) pour casser la forme circulaire.
   * Bordure intérieure en sable, intérieur en herbe, quelques rochers semés.
   * Tout est déterministe : même seed → même île à chaque visite.
   */
  private generateIsland(): void {
    const cx = this.cols / 2;
    const cy = this.rows / 2;
    // Rayon de base : limité par la plus petite dimension de la grille
    const baseRadius = Math.min(this.cols, this.rows) * 0.42;

    // Initialiser la grille en eau
    this.grid = Array.from({ length: this.rows }, () =>
      Array<Tile>(this.cols).fill('water'),
    );

    // 1. Forme de l'île — ellipse + perturbation sinusoïdale par angle (déterministe)
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const dx = (x + 0.5 - cx);
        // Compense aspect ratio (rows souvent ≠ cols)
        const aspect = this.cols / this.rows;
        const dy = (y + 0.5 - cy) * aspect;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);
        // Perturbation : 3 sinusoïdes de fréquences/phases différentes
        const noise =
          Math.sin(angle * 3 + 0.7) * 0.10 +
          Math.sin(angle * 5 + 2.3) * 0.06 +
          Math.sin(angle * 7 + 4.1) * 0.04;
        const radius = baseRadius * (1 + noise);

        if (dist < radius - 1.8) {
          this.grid[y][x] = 'grass';
        } else if (dist < radius) {
          this.grid[y][x] = 'sand';
        }
      }
    }

    // 2. Rochers déterministes — quelques tuiles d'herbe deviennent des rochers.
    //    Sélection via hash de coordonnées pour rester stable et clairsemé.
    const rockCandidates: Array<[number, number]> = [];
    for (let y = 1; y < this.rows - 1; y++) {
      for (let x = 1; x < this.cols - 1; x++) {
        if (this.grid[y][x] !== 'grass') continue;
        const h = hash2(x, y);
        // ~3% des tuiles d'herbe → rocher (densité raisonnable)
        if (h % 33 === 0) rockCandidates.push([x, y]);
      }
    }
    for (const [x, y] of rockCandidates) {
      // Évite les rochers juste à côté du sable (laisse plage dégagée)
      if (this.hasNeighbor(x, y, 'sand')) continue;
      this.grid[y][x] = 'rock';
    }

    // 3. Petit sentier en croix au centre (visuel — relie les futurs bâtiments).
    //    Les sentiers vers les bâtiments sont tracés plus tard dans placeBuildings().
    this.carvePath(Math.floor(cx), Math.floor(cy));
  }

  /** Trace une croix de tuiles "path" autour du centre */
  private carvePath(cx: number, cy: number): void {
    const ext = Math.min(3, Math.floor(Math.min(this.cols, this.rows) / 6));
    for (let dx = -ext; dx <= ext; dx++) {
      const x = cx + dx;
      if (this.inGrid(x, cy) && this.grid[cy][x] === 'grass') {
        this.grid[cy][x] = 'path';
      }
    }
    for (let dy = -ext; dy <= ext; dy++) {
      const y = cy + dy;
      if (this.inGrid(cx, y) && this.grid[y][cx] === 'grass') {
        this.grid[y][cx] = 'path';
      }
    }
  }

  /**
   * Trace un sentier rectiligne (en escalier — d'abord vertical, puis horizontal)
   * entre deux tuiles. Convertit les rochers/herbes traversées en "path".
   * L'eau et le sable restent intacts (le chemin s'arrête naturellement).
   */
  private carvePathBetween(sx: number, sy: number, tx: number, ty: number): void {
    let x = sx, y = sy;
    const setPath = (cx: number, cy: number) => {
      if (!this.inGrid(cx, cy)) return;
      const t = this.grid[cy][cx];
      if (t === 'grass' || t === 'rock') this.grid[cy][cx] = 'path';
    };
    // Phase verticale
    while (y !== ty) {
      y += y < ty ? 1 : -1;
      setPath(x, y);
    }
    // Phase horizontale
    while (x !== tx) {
      x += x < tx ? 1 : -1;
      setPath(x, y);
    }
  }

  private hasNeighbor(x: number, y: number, type: Tile): boolean {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        if (this.inGrid(x + dx, y + dy) && this.grid[y + dy][x + dx] === type) return true;
      }
    }
    return false;
  }

  private inGrid(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.cols && y < this.rows;
  }

  // ============================================================
  // BÂTIMENTS — PLACEMENT (Sprint A2 + B1)
  // ============================================================

  /**
   * Calcule pour chaque bâtiment :
   *   - sa tile d'ancrage (la plus proche de l'anchor normalisé qui soit
   *     marchable ou herbe, jamais eau)
   *   - sa tile d'approche (devant le bâtiment, marchable)
   *   - le sentier qui relie cette approche au centre de l'île
   *
   * On NE place PAS encore les sprites — on prépare juste la structure.
   * Les sprites sont ajoutés par drawBuildings() après le rendu des tuiles.
   */
  private placeBuildings(): void {
    this.buildings = [];
    const cx = Math.floor(this.cols / 2);
    const cy = Math.floor(this.rows / 2);

    for (const def of BUILDINGS) {
      const targetX = Math.round(def.anchor.x * (this.cols - 1));
      const targetY = Math.round(def.anchor.y * (this.rows - 1));
      const tile = this.findBuildingTile(targetX, targetY, def.forceSand === true);
      if (!tile) continue; // ne devrait pas arriver — île trop petite ?

      // Type de tile sous le bâtiment
      if (def.forceSand === true) {
        // Chantier Naval : toujours sur la plage (sable). Si on est dans l'eau
        // proche du rivage, on force en sable pour planter le sprite.
        this.grid[tile.y][tile.x] = 'sand';
      } else {
        if (this.grid[tile.y][tile.x] === 'water') this.grid[tile.y][tile.x] = 'sand';
        else if (this.grid[tile.y][tile.x] === 'rock') this.grid[tile.y][tile.x] = 'grass';
      }

      // Tile d'approche : un cran sous le bâtiment (sa "porte" visible)
      const approach = this.findApproachTile(tile.x, tile.y);

      this.buildings.push({
        def,
        sprite: null as unknown as Phaser.GameObjects.Image, // créé par drawBuildings
        label: null as unknown as Phaser.GameObjects.Text,
        built: this.isBuilt(def),
        locked: def.builtCheck.kind === 'locked',
        tile,
        approachTile: approach,
      });

      // Sentier vers le centre — depuis la tuile d'approche
      this.carvePathBetween(approach.x, approach.y, cx, cy);
    }
  }

  /** Cherche autour de (x,y) une tile marchable ou herbe (jamais eau).
   *  Si `preferSand` est vrai (chantier naval), privilégie une tile de sable. */
  private findBuildingTile(x: number, y: number, preferSand = false): { x: number; y: number } | null {
    const isOk = (nx: number, ny: number): boolean => {
      if (!this.inGrid(nx, ny)) return false;
      const t = this.grid[ny][nx];
      if (preferSand) {
        // Pour chantier naval : on accepte sable, water-adjacent-to-sand sera forcé.
        return t === 'sand' || t === 'water';
      }
      return t !== 'water';
    };
    if (isOk(x, y)) return { x, y };
    // Recherche en spirale
    for (let r = 1; r < Math.max(this.cols, this.rows); r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const nx = x + dx, ny = y + dy;
          if (isOk(nx, ny)) return { x: nx, y: ny };
        }
      }
    }
    return null;
  }

  /**
   * Trouve une tile d'approche marchable au sud du bâtiment (priorité), sinon
   * dans les 4 directions cardinales. Garantit que le sprite a une "porte"
   * sur laquelle marcher pour déclencher l'interaction.
   */
  private findApproachTile(bx: number, by: number): { x: number; y: number } {
    const candidates: Array<[number, number]> = [
      [bx, by + 1], [bx + 1, by], [bx - 1, by], [bx, by - 1],
      [bx + 1, by + 1], [bx - 1, by + 1],
    ];
    for (const [x, y] of candidates) {
      if (this.inGrid(x, y) && this.grid[y][x] !== 'water') {
        if (this.grid[y][x] !== 'path' && this.grid[y][x] !== 'sand') {
          this.grid[y][x] = 'path';
        }
        return { x, y };
      }
    }
    return { x: bx, y: by + 1 }; // fallback (cliquable même si bizarre)
  }

  /** Vérifie si un bâtiment est construit selon le système d'upgrades. */
  private isBuilt(def: IslandBuilding): boolean {
    if (def.builtCheck.kind === 'always') return true;
    if (def.builtCheck.kind === 'locked') return false; // chantier naval = jamais construit
    const level = this.camp.getUpgrades().getBuildingLevel(def.builtCheck.buildingId);
    return level > 0;
  }

  // ============================================================
  // OBJETS INTERACTIFS — PLACEMENT (Sprint B1)
  // ============================================================

  /** Place les 4 objets diégétiques (portail raid, panneau, table nav, feu de camp). */
  private placeInteractables(): void {
    this.interactables = [];
    const cx = Math.floor(this.cols / 2);
    const cy = Math.floor(this.rows / 2);

    for (const def of INTERACTABLES) {
      const targetX = Math.round(def.anchor.x * (this.cols - 1));
      const targetY = Math.round(def.anchor.y * (this.rows - 1));
      const tile = this.findBuildingTile(targetX, targetY, false);
      if (!tile) continue;

      // Force la tile sous l'objet en herbe si rocher
      if (this.grid[tile.y][tile.x] === 'rock') this.grid[tile.y][tile.x] = 'grass';

      const approach = this.findApproachTile(tile.x, tile.y);
      this.interactables.push({
        def,
        sprite: null as unknown as Phaser.GameObjects.Image,
        label: null as unknown as Phaser.GameObjects.Text,
        tile,
        approachTile: approach,
      });

      // Sentier vers le centre depuis la tile d'approche
      this.carvePathBetween(approach.x, approach.y, cx, cy);
    }
  }

  // ============================================================
  // BÂTIMENTS — RENDU & INTERACTION
  // ============================================================

  private drawBuildings(): void {
    const size = IslandTileRenderer.BUILDING_SIZE;
    for (const pb of this.buildings) {
      const center = this.tileToWorld(pb.tile.x, pb.tile.y);
      // État sprite : 'locked' pour le chantier naval, 'built' ou 'foundation' sinon.
      const state: 'built' | 'foundation' | 'locked' =
        pb.locked ? 'locked' : (pb.built ? 'built' : 'foundation');
      const sprite = this.add.image(
        center.x,
        center.y + TILE_SIZE / 2,
        IslandTileRenderer.buildingKey(pb.def.id, state),
      );
      sprite.setOrigin(0.5, 1);
      // Depth basé sur la base du sprite (pixel y) pour que les éléments du bas
      // passent devant ceux du haut. Capitaine partage le même barème.
      sprite.setDepth(2 + sprite.y / 1000);

      // Hit area généreuse : 44px min côté tactile, agrandie au-delà du sprite
      const hitW = Math.max(44, size);
      const hitH = Math.max(44, size + 8);
      sprite.setInteractive(
        new Phaser.Geom.Rectangle(
          (size - hitW) / 2,
          (size - hitH),
          hitW,
          hitH,
        ),
        Phaser.Geom.Rectangle.Contains,
      );
      // Hand cursor desktop (les mobiles ignorent)
      sprite.input!.cursor = 'pointer';

      sprite.on('pointerdown', () => this.onBuildingClick(pb));
      sprite.on('pointerover', () => {
        if (pb.built && !pb.locked) sprite.setTint(0xffe699);
        pb.label.setAlpha(1);
      });
      sprite.on('pointerout', () => {
        sprite.clearTint();
        pb.label.setAlpha(pb.locked ? 0.55 : (pb.built ? 0.65 : 0.45));
      });

      // Label au-dessus du sprite (visible en permanence en discret, plein au hover)
      const labelY = sprite.y - size - 4;
      const labelColor = pb.locked ? '#e67e22' : (pb.built ? '#ecf0f1' : '#7f8c8d');
      const label = this.add.text(center.x, labelY, pb.def.label, {
        fontSize: '10px',
        color: labelColor,
        fontFamily: FONT_BODY,
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      }).setOrigin(0.5, 1).setDepth(20)
        .setAlpha(pb.locked ? 0.55 : (pb.built ? 0.65 : 0.45));

      pb.sprite = sprite;
      pb.label = label;
    }
  }

  /**
   * Au clic sur un bâtiment :
   *  - verrouillé (chantier naval) → tooltip + shake immédiat (pas de marche)
   *  - non construit → tooltip "À construire dans l'Atelier"
   *  - construit avec action 'scene' → marche jusqu'à la tile d'approche puis ouvre la scène
   *  - construit avec action 'tooltip' → marche puis affiche le message (cas Boutique)
   */
  private onBuildingClick(pb: PlacedBuilding): void {
    if (pb.locked) {
      // Chantier Naval — feedback immédiat, pas de marche.
      if (pb.def.action.kind === 'locked') {
        this.showTooltip(pb.sprite.x, pb.sprite.y - 8, pb.def.action.message);
      }
      this.shakeSprite(pb.sprite);
      return;
    }
    if (!pb.built) {
      this.showTooltip(pb.sprite.x, pb.sprite.y - 8, 'À construire dans l\'Atelier');
      return;
    }
    this.pendingArrival = { kind: 'building', building: pb };
    this.moveCaptainTo(pb.approachTile.x, pb.approachTile.y);
  }

  /** Déclenche l'action du bâtiment une fois le capitaine arrivé devant. */
  private triggerBuildingAction(pb: PlacedBuilding): void {
    const action = pb.def.action;
    if (action.kind === 'scene') {
      startSceneWithFade(this, action.sceneKey, { camp: this.camp, meta: this.meta });
    } else if (action.kind === 'tooltip') {
      this.showTooltip(pb.sprite.x, pb.sprite.y - 8, action.message);
    }
    // 'locked' déjà géré au clic, pas de déclenchement à l'arrivée.
  }

  // ============================================================
  // OBJETS INTERACTIFS — RENDU & INTERACTION (Sprint B1)
  // ============================================================

  private drawInteractables(): void {
    const size = IslandTileRenderer.OBJECT_SIZE;
    for (const pi of this.interactables) {
      const center = this.tileToWorld(pi.tile.x, pi.tile.y);
      const sprite = this.add.image(
        center.x,
        center.y + TILE_SIZE / 2,
        IslandTileRenderer.interactableKey(pi.def.id),
      );
      sprite.setOrigin(0.5, 1);
      sprite.setDepth(2 + sprite.y / 1000);

      // Animation discrète pour le feu de camp (vacillement)
      if (pi.def.id === 'campfire') {
        this.tweens.add({
          targets: sprite,
          alpha: { from: 0.92, to: 1 },
          duration: 380,
          yoyo: true,
          repeat: -1,
        });
        // Sprint A4 — particules fumée + étincelles
        this.spawnCampfireParticles(sprite.x, sprite.y - IslandTileRenderer.OBJECT_SIZE * 0.55);
      }

      const hitW = Math.max(44, size);
      const hitH = Math.max(44, size + 6);
      sprite.setInteractive(
        new Phaser.Geom.Rectangle(
          (size - hitW) / 2,
          (size - hitH),
          hitW,
          hitH,
        ),
        Phaser.Geom.Rectangle.Contains,
      );
      sprite.input!.cursor = 'pointer';

      sprite.on('pointerdown', () => this.onInteractableClick(pi));
      sprite.on('pointerover', () => {
        sprite.setTint(0xffe699);
        pi.label.setAlpha(1);
      });
      sprite.on('pointerout', () => {
        sprite.clearTint();
        pi.label.setAlpha(0.65);
      });

      const labelY = sprite.y - size - 4;
      const label = this.add.text(center.x, labelY, pi.def.label, {
        fontSize: '10px',
        color: '#ecf0f1',
        fontFamily: FONT_BODY,
        backgroundColor: '#000000aa',
        padding: { x: 4, y: 2 },
      }).setOrigin(0.5, 1).setDepth(20).setAlpha(0.65);

      pi.sprite = sprite;
      pi.label = label;
    }
  }

  /** Clic sur un objet diégétique : marche vers son approche puis déclenche l'action. */
  private onInteractableClick(pi: PlacedInteractable): void {
    this.pendingArrival = { kind: 'interactable', interactable: pi };
    this.moveCaptainTo(pi.approachTile.x, pi.approachTile.y);
  }

  /** Déclenche l'action d'un objet à l'arrivée du capitaine. */
  private triggerInteractableAction(pi: PlacedInteractable): void {
    const action = pi.def.action;
    switch (action.kind) {
      case 'raid':
        startSceneWithFade(this, 'RunPrepScene', { camp: this.camp, meta: this.meta });
        break;
      case 'scene':
        startSceneWithFade(this, action.sceneKey, action.sceneData ?? { camp: this.camp, meta: this.meta });
        break;
      case 'settings':
        startSceneWithFade(this, 'SettingsScene', { returnTo: 'IslandCampScene', camp: this.camp, meta: this.meta });
        break;
      case 'stats':
        startSceneWithFade(this, 'StatsScene', { meta: this.meta, fromCamp: true, camp: this.camp });
        break;
      case 'signboard_menu':
        this.showSignboardMenu();
        break;
    }
  }

  // ============================================================
  // DÉCORATIONS — RENDU (Sprint B1)
  // ============================================================

  /** Pose les sprites décoratifs aux positions normalisées définies. Non interactifs. */
  private drawDecorations(): void {
    for (const dec of DECORATIONS) {
      const tx = Math.round(dec.anchor.x * (this.cols - 1));
      const ty = Math.round(dec.anchor.y * (this.rows - 1));
      if (!this.inGrid(tx, ty)) continue;
      // On évite de planter une déco dans l'eau ou sur un chemin actif.
      const t = this.grid[ty][tx];
      if (t === 'water') continue;
      // Évite aussi de masquer une approche de bâtiment ou un objet déjà posé.
      if (this.tileOccupiedByBuildingOrInteractable(tx, ty)) continue;

      const center = this.tileToWorld(tx, ty);
      const sprite = this.add.image(
        center.x,
        center.y + TILE_SIZE / 2,
        IslandTileRenderer.decorationKey(dec.id),
      );
      sprite.setOrigin(0.5, 1);
      sprite.setDepth(2 + sprite.y / 1000);
      // Les décorations sont décoratives — pas d'interactivité.
    }
  }

  /** Vrai si la tile est occupée par un bâtiment ou un objet interactif déjà placé. */
  private tileOccupiedByBuildingOrInteractable(tx: number, ty: number): boolean {
    for (const pb of this.buildings) {
      if (pb.tile.x === tx && pb.tile.y === ty) return true;
      if (pb.approachTile.x === tx && pb.approachTile.y === ty) return true;
    }
    for (const pi of this.interactables) {
      if (pi.tile.x === tx && pi.tile.y === ty) return true;
      if (pi.approachTile.x === tx && pi.approachTile.y === ty) return true;
    }
    return false;
  }

  // ============================================================
  // MENU DU PANNEAU D'AFFICHAGE (Sprint B1)
  // ============================================================

  /**
   * Affiche un overlay modal listant les actions community / méta :
   *   - Classement en ligne
   *   - Patchnotes
   *   - Roadmap
   *   - Signaler un bug
   *   - Retour menu principal
   *   - Ancien camp (escape hatch tant que la refonte est WIP)
   */
  private showSignboardMenu(): void {
    const depthBase = 200;
    const overlay = this.add.rectangle(GAME_CX, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0.65).setDepth(depthBase);
    overlay.setInteractive();

    const panelW = Math.min(320, GAME_W - 40);
    const panelH = 360;
    const panelY = GAME_H / 2;
    const panel = this.add.rectangle(GAME_CX, panelY, panelW, panelH, 0x1a2540).setDepth(depthBase + 1);
    panel.setStrokeStyle(2, 0xf1c40f);
    panel.setInteractive(); // bloque le clic à travers

    const title = this.add.text(GAME_CX, panelY - panelH / 2 + 22, '📋 Tableau d\'affichage', {
      fontSize: '15px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(depthBase + 2);

    const subtitle = this.add.text(GAME_CX, panelY - panelH / 2 + 44, 'Le carnet du Capitaine', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(depthBase + 2);

    type MenuEntry = { label: string; color: string; onClick: () => void };
    const entries: MenuEntry[] = [
      {
        label: '🏆 Classement en ligne',
        color: '#1abc9c',
        onClick: () => {
          this.cleanupSignboard(elements);
          startSceneWithFade(this, 'LeaderboardScene', { returnTo: 'IslandCampScene' });
        },
      },
      {
        label: '📋 Patchnotes',
        color: '#3498db',
        onClick: () => {
          this.cleanupSignboard(elements);
          startSceneWithFade(this, 'PatchNotesScene', { returnTo: 'IslandCampScene' });
        },
      },
      {
        label: '📜 Roadmap',
        color: '#f1c40f',
        onClick: () => {
          this.cleanupSignboard(elements);
          startSceneWithFade(this, 'RoadmapScene', { returnTo: 'IslandCampScene' });
        },
      },
      {
        label: '🐛 Signaler un bug',
        color: '#e74c3c',
        onClick: async () => {
          this.cleanupSignboard(elements);
          const canvas = this.game.canvas;
          const screenshot = await captureCanvas(canvas).catch(() => null);
          showBugReportOverlay(this.scene.key, undefined, undefined, screenshot);
        },
      },
      {
        label: '🏠 Retour menu principal',
        color: '#e67e22',
        onClick: () => {
          this.cleanupSignboard(elements);
          startSceneWithFade(this, 'BootScene');
        },
      },
      {
        label: '📜 Ancien camp (escape hatch)',
        color: '#bdc3c7',
        onClick: () => {
          this.cleanupSignboard(elements);
          startSceneWithFade(this, 'CampScene', { camp: this.camp, meta: this.meta });
        },
      },
    ];

    const buttonObjs: Phaser.GameObjects.Text[] = [];
    let by = panelY - panelH / 2 + 80;
    const spacing = 38;
    for (const entry of entries) {
      const btn = this.add.text(GAME_CX, by, entry.label, {
        fontSize: '12px', color: entry.color, fontFamily: FONT_BODY,
        backgroundColor: '#0a1428', padding: { x: 14, y: 8 },
      }).setOrigin(0.5).setDepth(depthBase + 2);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerover', () => btn.setBackgroundColor('#162a4a'));
      btn.on('pointerout', () => btn.setBackgroundColor('#0a1428'));
      btn.on('pointerdown', entry.onClick);
      addHoverPulse(this, btn);
      buttonObjs.push(btn);
      by += spacing;
    }

    // Bouton fermer en bas du panneau
    const closeBtn = this.add.text(GAME_CX, panelY + panelH / 2 - 22, '✖ Fermer', {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 10, y: 6 },
    }).setOrigin(0.5).setDepth(depthBase + 2);
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.on('pointerover', () => closeBtn.setColor('#bdc3c7'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#7f8c8d'));

    const elements: Phaser.GameObjects.GameObject[] = [overlay, panel, title, subtitle, closeBtn, ...buttonObjs];

    closeBtn.on('pointerdown', () => this.cleanupSignboard(elements));
    overlay.on('pointerdown', () => this.cleanupSignboard(elements));
  }

  private cleanupSignboard(elements: Phaser.GameObjects.GameObject[]): void {
    for (const el of elements) el.destroy();
  }

  // ============================================================
  // TOOLTIP & SHAKE FX
  // ============================================================

  /** Petit tooltip qui fade — réutilisé pour "non construit", "marchand absent", "verrouillé". */
  private showTooltip(wx: number, wy: number, msg: string): void {
    const tip = this.add.text(wx, wy, msg, {
      fontSize: '11px',
      color: '#f1c40f',
      fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2eee',
      padding: { x: 8, y: 4 },
      align: 'center',
    }).setOrigin(0.5, 1).setDepth(30);
    this.tweens.add({
      targets: tip,
      y: wy - 18,
      alpha: 0,
      duration: 1600,
      delay: 400,
      ease: 'Sine.easeIn',
      onComplete: () => tip.destroy(),
    });
  }

  /** Petit shake du sprite (effet "verrouillé" sur le cadenas du chantier naval). */
  private shakeSprite(sprite: Phaser.GameObjects.Image): void {
    const baseX = sprite.x;
    this.tweens.add({
      targets: sprite,
      x: { from: baseX - 3, to: baseX + 3 },
      duration: 40,
      yoyo: true,
      repeat: 4,
      onComplete: () => { sprite.x = baseX; },
    });
  }

  // ============================================================
  // RENDU DES TUILES
  // ============================================================

  private drawTiles(): void {
    this.waterImages = [];
    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const tile = this.grid[y][x];
        const tx = this.gridOriginX + x * TILE_SIZE + TILE_SIZE / 2;
        const ty = this.gridOriginY + y * TILE_SIZE + TILE_SIZE / 2;
        // Image centrée — la taille naturelle est déjà TILE_SIZE×TILE_SIZE
        const img = this.add.image(tx, ty, IslandTileRenderer.tileKey(tile)).setDepth(0);
        // Évite tout flou (les textures sont nearest mais on s'assure)
        img.setOrigin(0.5);
        // Sprint A4 — collecte les tuiles d'eau pour les animer en frame swap.
        if (tile === 'water') this.waterImages.push(img);
      }
    }
    // Sprint A4 — démarre le toggle d'eau (1100 ms ≈ rythme de respiration).
    this.startWaterAnimation();
  }

  /**
   * Sprint A4 — alterne toutes les tuiles d'eau entre `water` et `water_alt`
   * toutes les 1100 ms. Décalage léger par x+y pour éviter un swap synchrone
   * trop régulier (effet damier discret).
   */
  private startWaterAnimation(): void {
    if (this.waterImages.length === 0) return;
    this.waterToggleEvent?.remove(false);
    this.waterToggleEvent = this.time.addEvent({
      delay: 1100,
      loop: true,
      callback: () => {
        this.waterFrameAlt = !this.waterFrameAlt;
        const baseKey = IslandTileRenderer.tileKey('water');
        const altKey = IslandTileRenderer.waterAltKey();
        for (const img of this.waterImages) {
          // Décale d'1 cycle sur ~50 % des tuiles via parité x+y de la position
          // (mod sur les pixels — entiers en pratique car positionnés sur grille).
          const phase = ((Math.floor(img.x / TILE_SIZE) + Math.floor(img.y / TILE_SIZE)) % 2) === 0;
          const useAlt = phase ? this.waterFrameAlt : !this.waterFrameAlt;
          img.setTexture(useAlt ? altKey : baseKey);
        }
      },
    });
  }

  // ============================================================
  // CAPITAINE
  // ============================================================

  private spawnCaptain(): void {
    // Spawn au centre, sur la première tile marchable (devrait être un sentier)
    const start = this.findSpawnTile();
    const pos = this.tileToWorld(start.x, start.y);
    this.captain = this.add.image(pos.x, pos.y, IslandTileRenderer.captainKey('idle'));
    this.captain.setOrigin(0.5, 0.7); // pieds vers le bas de la tile pour effet 2.5D léger
    this.captain.setDepth(2 + this.captain.y / 1000);
  }

  private findSpawnTile(): { x: number; y: number } {
    const cx = Math.floor(this.cols / 2);
    const cy = Math.floor(this.rows / 2);
    if (this.inGrid(cx, cy) && WALKABLE_TILES.has(this.grid[cy][cx])) return { x: cx, y: cy };
    // Spirale de fallback si la tuile centrale n'est pas marchable
    for (let r = 1; r < Math.max(this.cols, this.rows); r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const x = cx + dx, y = cy + dy;
          if (this.inGrid(x, y) && WALKABLE_TILES.has(this.grid[y][x])) return { x, y };
        }
      }
    }
    return { x: cx, y: cy };
  }

  private tileToWorld(tx: number, ty: number): { x: number; y: number } {
    return {
      x: this.gridOriginX + tx * TILE_SIZE + TILE_SIZE / 2,
      y: this.gridOriginY + ty * TILE_SIZE + TILE_SIZE / 2,
    };
  }

  private worldToTile(wx: number, wy: number): { x: number; y: number } | null {
    const tx = Math.floor((wx - this.gridOriginX) / TILE_SIZE);
    const ty = Math.floor((wy - this.gridOriginY) / TILE_SIZE);
    if (!this.inGrid(tx, ty)) return null;
    return { x: tx, y: ty };
  }

  // ============================================================
  // INPUT — POINT & CLICK
  // ============================================================

  private installInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]) => {
      // Ignore les clics dans le header
      if (pointer.y < HEADER_H) return;
      // Si on a cliqué sur un objet interactif (bâtiment, bouton), laisse son handler agir
      if (currentlyOver && currentlyOver.length > 0) return;
      const tile = this.worldToTile(pointer.worldX, pointer.worldY);
      if (!tile) return;
      const type = this.grid[tile.y][tile.x];
      if (!WALKABLE_TILES.has(type)) {
        this.flashUnwalkable(pointer.worldX, pointer.worldY);
        return;
      }
      // Clic sur le sol → annule toute arrivée prévue (l'utilisateur change d'idée)
      this.pendingArrival = null;
      this.moveCaptainTo(tile.x, tile.y);
    });
  }

  /**
   * Déplace le capitaine en ligne droite (tween linéaire) jusqu'à la tile cible.
   * Pas de pathfinding — si le segment droit traverse de l'eau ou un rocher,
   * on tronque la cible à la dernière tile marchable rencontrée (raycast simple).
   */
  private moveCaptainTo(tileX: number, tileY: number): void {
    // Cancel un mouvement en cours
    this.captainMoveTween?.stop();
    this.cancelCaptainAnim();

    // Tile de départ du capitaine
    const startTile = this.worldToTile(this.captain.x, this.captain.y) ?? { x: tileX, y: tileY };
    const safe = this.raycastToWalkable(startTile.x, startTile.y, tileX, tileY);
    const target = this.tileToWorld(safe.x, safe.y);

    const dx = target.x - this.captain.x;
    const dy = target.y - this.captain.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) {
      // Capitaine déjà à destination — déclenche directement si une arrivée est prévue.
      this.maybeTriggerArrival();
      return;
    }
    const duration = Math.max(60, (dist / CAPTAIN_SPEED) * 1000);

    // Anim 2-frames : alterne idle/walk toutes les 180ms
    this.startCaptainAnim();

    this.captainMoveTween = this.tweens.add({
      targets: this.captain,
      x: target.x,
      y: target.y,
      duration,
      ease: 'Linear',
      onUpdate: () => {
        // Met à jour la profondeur en continu pour passer devant/derrière les bâtiments
        this.captain.setDepth(2 + this.captain.y / 1000);
      },
      onComplete: () => {
        this.cancelCaptainAnim();
        this.captain.setTexture(IslandTileRenderer.captainKey('idle'));
        this.captainMoveTween = null;
        this.maybeTriggerArrival();
      },
    });
  }

  /**
   * Si une arrivée est en attente (clic bâtiment ou objet diégétique) et que le
   * capitaine est suffisamment proche, déclenche l'action. Sinon on l'oublie
   * (cas raycast tronqué — cible inaccessible pour une raison ou une autre).
   */
  private maybeTriggerArrival(): void {
    const pending = this.pendingArrival;
    this.pendingArrival = null;
    if (!pending) return;
    const approachTile = pending.kind === 'building'
      ? pending.building.approachTile
      : pending.interactable.approachTile;
    const tgt = this.tileToWorld(approachTile.x, approachTile.y);
    const dx = this.captain.x - tgt.x;
    const dy = this.captain.y - tgt.y;
    if (Math.sqrt(dx * dx + dy * dy) <= ARRIVAL_THRESHOLD) {
      if (pending.kind === 'building') {
        this.triggerBuildingAction(pending.building);
      } else {
        this.triggerInteractableAction(pending.interactable);
      }
    }
  }

  /**
   * Trace une ligne discrète entre (sx,sy) et (tx,ty) en pas demi-tile, et
   * renvoie la dernière coordonnée marchable atteinte (Bresenham simplifié).
   */
  private raycastToWalkable(sx: number, sy: number, tx: number, ty: number): { x: number; y: number } {
    const steps = Math.max(Math.abs(tx - sx), Math.abs(ty - sy)) * 2 || 1;
    let lastOk = { x: sx, y: sy };
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const cx = Math.round(sx + (tx - sx) * t);
      const cy = Math.round(sy + (ty - sy) * t);
      if (!this.inGrid(cx, cy)) break;
      if (!WALKABLE_TILES.has(this.grid[cy][cx])) break;
      lastOk = { x: cx, y: cy };
    }
    return lastOk;
  }

  private startCaptainAnim(): void {
    let frame: 'idle' | 'walk' = 'walk';
    this.captain.setTexture(IslandTileRenderer.captainKey(frame));
    this.captainAnimEvent = this.time.addEvent({
      delay: 180,
      loop: true,
      callback: () => {
        frame = frame === 'walk' ? 'idle' : 'walk';
        this.captain.setTexture(IslandTileRenderer.captainKey(frame));
      },
    });
  }

  private cancelCaptainAnim(): void {
    this.captainAnimEvent?.remove(false);
    this.captainAnimEvent = null;
  }

  // ============================================================
  // CYCLE JOUR/NUIT — overlay teinté selon heure réelle (Sprint A4)
  // ============================================================

  /**
   * Pose un rectangle plein écran teinté pour suggérer l'heure de la journée.
   * 4 phases en fonction de l'heure locale du joueur :
   *   - 22h–6h : nuit (bleu nuit, alpha 0.30)
   *   - 6h–8h  : lever (orange chaud, alpha 0.18)
   *   - 8h–18h : journée (pas d'overlay)
   *   - 18h–22h : coucher (orange profond, alpha 0.22)
   *
   * Subtilité : un léger pulse d'alpha (±15 %) sur 60 s pour donner une
   * "respiration" ambiante sans devenir une distraction.
   *
   * NB : pas de transition continue heure par heure pendant la session — on
   * sample une seule fois à l'entrée de la scène. Sinon il faudrait écouter
   * un changement d'heure pour rester juste, et c'est overkill pour un effet
   * d'ambiance.
   */
  private applyTimeOfDayOverlay(): void {
    const hour = new Date().getHours();
    let color = 0x000000;
    let alpha = 0;
    if (hour >= 22 || hour < 6) {
      color = 0x0a1a40; alpha = 0.30;          // nuit
    } else if (hour < 8) {
      color = 0xffb37a; alpha = 0.18;          // lever
    } else if (hour < 18) {
      color = 0xffffff; alpha = 0;              // pleine journée
    } else if (hour < 22) {
      color = 0xff8c42; alpha = 0.22;          // coucher
    }
    if (alpha === 0) return;

    // Overlay au-dessus des sprites mais sous les bulles (depth 50+).
    const rect = this.add.rectangle(GAME_CX, GAME_H / 2, GAME_W, GAME_H, color, alpha);
    rect.setDepth(49);
    // Le rectangle n'intercepte pas les inputs (default = pas d'interactivité).
    this.timeOfDayOverlay = rect;

    // Respiration douce : ±15 % d'alpha sur 60 s, yoyo infini.
    const baseAlpha = alpha;
    this.tweens.add({
      targets: rect,
      alpha: { from: baseAlpha * 0.85, to: baseAlpha * 1.15 },
      duration: 60000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  // ============================================================
  // FEU DE CAMP — particules fumée + étincelles (Sprint A4)
  // ============================================================

  /**
   * Émet en continu deux flux de particules à la position du feu de camp :
   *   - fumée grise qui monte lentement, fade vers l'alpha 0 en haut
   *   - étincelles orange/jaune qui jaillissent vite, courte durée de vie
   *
   * Les émitters tournent indéfiniment ; ils sont stoppés et détruits dans
   * `shutdown()` pour éviter les fuites quand on quitte la scène.
   */
  private spawnCampfireParticles(x: number, y: number): void {
    ensureParticleTexture(this);
    // Fumée — particules grises lentes qui montent
    const smoke = this.add.particles(x, y, 'particle_dot', {
      speed: { min: 8, max: 20 },
      angle: { min: 260, max: 280 },          // ≈ vers le haut, légère dispersion
      scale: { start: 0.5, end: 1.4 },
      alpha: { start: 0.35, end: 0 },
      lifespan: { min: 1400, max: 2200 },
      frequency: 220,                          // 1 particule toutes les 220 ms
      tint: [0x7f8c8d, 0xbdc3c7, 0x95a5a6],
    });
    smoke.setDepth(15);                        // au-dessus du feu mais sous les bulles
    this.campfireEmitters.push(smoke);

    // Étincelles — burst rapide orange/jaune
    const sparks = this.add.particles(x, y + 4, 'particle_dot', {
      speed: { min: 30, max: 70 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.35, end: 0.05 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 350, max: 700 },
      frequency: 130,
      tint: [0xe67e22, 0xf1c40f, 0xff8c00],
      blendMode: 'ADD',
    });
    sparks.setDepth(16);
    this.campfireEmitters.push(sparks);
  }

  // ============================================================
  // MOUETTES — orbite déterministe au-dessus de l'île (Sprint A4)
  // ============================================================

  /**
   * Place 3 mouettes qui volent en cercles déterministes au-dessus de l'île.
   * Chaque mouette a son propre centre, rayon, période et phase pour éviter
   * un effet trop synchronisé. Animation pilotée par `update()` (parametric
   * position) + timer global pour le battement d'ailes (up ↔ down).
   */
  private spawnSeagulls(): void {
    this.seagulls = [];
    // Centres d'orbite : nord-ouest, centre-est, sud
    const centers = [
      { x: GAME_W * 0.3, y: HEADER_H + (GAME_H - HEADER_H) * 0.25 },
      { x: GAME_W * 0.7, y: HEADER_H + (GAME_H - HEADER_H) * 0.4 },
      { x: GAME_W * 0.5, y: HEADER_H + (GAME_H - HEADER_H) * 0.75 },
    ];
    for (let i = 0; i < centers.length; i++) {
      const sprite = this.add.image(centers[i].x, centers[i].y, IslandTileRenderer.seagullKey('up'));
      sprite.setOrigin(0.5).setDepth(45);
      // Petite ombre attachée (depth bas, alpha faible) — donne l'effet de hauteur.
      sprite.setData('cx', centers[i].x);
      sprite.setData('cy', centers[i].y);
      // Rayons & périodes variés pour rendre les trajectoires distinctes.
      sprite.setData('radius', 60 + i * 18);
      sprite.setData('period', 11000 + i * 1500);          // 11s, 12.5s, 14s
      sprite.setData('phase', (i * Math.PI * 2) / 3);
      this.seagulls.push(sprite);
    }
    // Battement d'ailes commun (toutes en sync — moins coûteux et invisible à
    // l'œil nu vu leur petite taille).
    this.seagullFrameEvent?.remove(false);
    this.seagullFrameEvent = this.time.addEvent({
      delay: 180,
      loop: true,
      callback: () => {
        this.seagullFrameDown = !this.seagullFrameDown;
        const key = IslandTileRenderer.seagullKey(this.seagullFrameDown ? 'down' : 'up');
        for (const s of this.seagulls) s.setTexture(key);
      },
    });
  }

  /** Met à jour la position des mouettes (orbite ellipsoïdale, écrasée 2:1). */
  override update(): void {
    if (this.seagulls.length === 0) return;
    const elapsed = this.time.now - this.islandStartTime;
    for (const s of this.seagulls) {
      const cx = s.getData('cx') as number;
      const cy = s.getData('cy') as number;
      const radius = s.getData('radius') as number;
      const period = s.getData('period') as number;
      const phase = s.getData('phase') as number;
      const t = (2 * Math.PI * elapsed) / period + phase;
      s.x = cx + radius * Math.cos(t);
      s.y = cy + radius * Math.sin(t) * 0.5; // ellipse aplatie (top-down)
      // Flip horizontal selon le sens de déplacement (dérivée = -sin)
      s.setFlipX(Math.sin(t) > 0);
    }
  }

  // ============================================================
  // SURVIVANTS — SPAWN, PATROUILLE & DIALOGUES (Sprint A3)
  // ============================================================

  /** Crée un sprite par survivant vivant et lance leur cycle de patrouille. */
  private spawnSurvivors(): void {
    this.survivors = [];
    // On garde uniquement les classes supportées par les sprites top-down.
    const alive = this.camp.getAliveSurvivors().filter(s =>
      s.class === 'warrior' || s.class === 'ranger' || s.class === 'medic',
    );

    for (let i = 0; i < alive.length; i++) {
      const data = alive[i];
      // Zone d'ancrage assignée round-robin sur les PATROL_ZONES définies.
      const zone = PATROL_ZONES[i % PATROL_ZONES.length];
      const targetX = Math.round(zone.x * (this.cols - 1));
      const targetY = Math.round(zone.y * (this.rows - 1));
      const anchor = this.findWalkableNear(targetX, targetY);
      if (!anchor) continue;

      const spriteClass = classToSpriteClass(data.class);
      const pos = this.tileToWorld(anchor.x, anchor.y);
      const sprite = this.add.image(pos.x, pos.y, IslandTileRenderer.survivorKey(spriteClass, 'idle'));
      sprite.setOrigin(0.5, 0.7);
      sprite.setDepth(2 + sprite.y / 1000);

      // Hit area généreuse pour le clic (44px min tactile, légèrement plus haut
      // pour cibler le buste plutôt que les pieds).
      const size = IslandTileRenderer.SURVIVOR_SIZE;
      const hitW = Math.max(44, size + 8);
      const hitH = Math.max(44, size + 8);
      sprite.setInteractive(
        new Phaser.Geom.Rectangle(
          (size - hitW) / 2,
          (size - hitH) - 2,
          hitW,
          hitH,
        ),
        Phaser.Geom.Rectangle.Contains,
      );
      sprite.input!.cursor = 'pointer';

      // Badge de niveau : "★" répété N fois en doré, au-dessus du sprite.
      // Caché si level <= 1 (la majorité des survivants frais — évite le bruit
      // visuel). Affiché à partir du niveau 2 pour signaler la progression.
      const levelBadge = data.level >= 2
        ? this.add.text(pos.x, pos.y - IslandTileRenderer.SURVIVOR_SIZE * 0.95, this.renderStars(data.level), {
            fontSize: '9px',
            color: '#f1c40f',
            fontFamily: FONT_BODY,
            fontStyle: 'bold',
          }).setOrigin(0.5, 1).setDepth(20).setAlpha(0.85)
        : null;

      const placed: PlacedSurvivor = {
        data,
        spriteClass,
        sprite,
        anchorTile: anchor,
        moveTween: null,
        animEvent: null,
        nextStepEvent: null,
        step: 0,
        bubble: null,
        lastBubbleAt: 0,
        levelBadge,
      };

      sprite.on('pointerover', () => {
        sprite.setTint(0xffe699);
        placed.levelBadge?.setAlpha(1);
      });
      sprite.on('pointerout', () => {
        sprite.clearTint();
        placed.levelBadge?.setAlpha(0.85);
      });
      sprite.on('pointerdown', () => this.onSurvivorClick(placed));

      this.survivors.push(placed);
      this.scheduleNextSurvivorStep(placed, true);
    }
  }

  /** Rendu des étoiles de niveau. Cap à 5 (LEVEL_CAP côté CampManager). */
  private renderStars(level: number): string {
    const clamped = Math.max(1, Math.min(5, level));
    return '★'.repeat(clamped);
  }

  /** Cherche une tile marchable proche de (x,y) en spirale (sand/grass/path). */
  private findWalkableNear(x: number, y: number): { x: number; y: number } | null {
    if (this.inGrid(x, y) && WALKABLE_TILES.has(this.grid[y][x])) return { x, y };
    for (let r = 1; r < Math.max(this.cols, this.rows); r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const nx = x + dx, ny = y + dy;
          if (this.inGrid(nx, ny) && WALKABLE_TILES.has(this.grid[ny][nx])) {
            return { x: nx, y: ny };
          }
        }
      }
    }
    return null;
  }

  /**
   * Planifie la prochaine étape de patrouille (idle d'abord, puis tween).
   * Déterministe via hash(survivorId, step) — les durées et cibles sont
   * reproductibles, ce qui aide au debug et évite les sauts visuels au respawn.
   */
  private scheduleNextSurvivorStep(p: PlacedSurvivor, initial: boolean): void {
    p.nextStepEvent?.remove(false);
    // Délai de pause idle (animation 2-frames = static frame "idle")
    const h = patrolHash(p.data.id, p.step);
    const range = PATROL_PAUSE_MAX - PATROL_PAUSE_MIN;
    const pause = initial
      ? 150 + (h % 800)              // léger délai au premier spawn
      : PATROL_PAUSE_MIN + (h % range);

    p.nextStepEvent = this.time.delayedCall(pause, () => this.stepSurvivor(p));
  }

  /**
   * Choisit une tile cible dans la zone de patrouille du survivant et lance un
   * tween linéaire. Si la cible n'est pas atteignable directement (raycast),
   * on s'arrête à la dernière tile marchable.
   */
  private stepSurvivor(p: PlacedSurvivor): void {
    p.step++;
    const h = patrolHash(p.data.id, p.step);
    // Offset dans le carré [-PATROL_RADIUS, +PATROL_RADIUS]
    const dx = (h % (PATROL_RADIUS * 2 + 1)) - PATROL_RADIUS;
    const dy = (((h / 7) | 0) % (PATROL_RADIUS * 2 + 1)) - PATROL_RADIUS;
    const targetTileX = p.anchorTile.x + dx;
    const targetTileY = p.anchorTile.y + dy;

    const startTile = this.worldToTile(p.sprite.x, p.sprite.y) ?? p.anchorTile;
    const safe = this.raycastToWalkable(startTile.x, startTile.y, targetTileX, targetTileY);
    const targetWorld = this.tileToWorld(safe.x, safe.y);

    const ddx = targetWorld.x - p.sprite.x;
    const ddy = targetWorld.y - p.sprite.y;
    const dist = Math.sqrt(ddx * ddx + ddy * ddy);
    if (dist < 4) {
      // Déjà à destination → reprogramme une pause sans bouger.
      this.scheduleNextSurvivorStep(p, false);
      return;
    }
    const duration = Math.max(120, (dist / SURVIVOR_SPEED) * 1000);

    // Anim 2-frames (toggle 220ms, légèrement plus lent que le capitaine 180ms).
    this.startSurvivorAnim(p);

    p.moveTween = this.tweens.add({
      targets: p.sprite,
      x: targetWorld.x,
      y: targetWorld.y,
      duration,
      ease: 'Linear',
      onUpdate: () => {
        p.sprite.setDepth(2 + p.sprite.y / 1000);
        // Met à jour la bulle si présente (suit le sprite).
        if (p.bubble) {
          p.bubble.x = p.sprite.x;
          p.bubble.y = p.sprite.y - IslandTileRenderer.SURVIVOR_SIZE - 8;
        }
        // Met à jour le badge de niveau (suit le sprite).
        if (p.levelBadge) {
          p.levelBadge.x = p.sprite.x;
          p.levelBadge.y = p.sprite.y - IslandTileRenderer.SURVIVOR_SIZE * 0.95;
        }
      },
      onComplete: () => {
        this.cancelSurvivorAnim(p);
        p.sprite.setTexture(IslandTileRenderer.survivorKey(p.spriteClass, 'idle'));
        p.moveTween = null;
        this.scheduleNextSurvivorStep(p, false);
      },
    });
  }

  private startSurvivorAnim(p: PlacedSurvivor): void {
    let frame: 'idle' | 'walk' = 'walk';
    p.sprite.setTexture(IslandTileRenderer.survivorKey(p.spriteClass, frame));
    p.animEvent = this.time.addEvent({
      delay: 220,
      loop: true,
      callback: () => {
        frame = frame === 'walk' ? 'idle' : 'walk';
        p.sprite.setTexture(IslandTileRenderer.survivorKey(p.spriteClass, frame));
      },
    });
  }

  private cancelSurvivorAnim(p: PlacedSurvivor): void {
    p.animEvent?.remove(false);
    p.animEvent = null;
  }

  /**
   * Clic sur un survivant → affiche une bulle de dialogue contextuelle.
   * Anti-spam : cooldown 600ms entre deux affichages, et une seule bulle active
   * par survivant à la fois (l'ancienne est détruite avant de re-créer).
   */
  private onSurvivorClick(p: PlacedSurvivor): void {
    const now = this.time.now;
    if (now - p.lastBubbleAt < BUBBLE_COOLDOWN) return;
    p.lastBubbleAt = now;
    const line = pickDialogue(p.data.id, p.spriteClass);
    this.showSurvivorBubble(p, line);
  }

  /** Construit la bulle (nom doré + message + cadre arrondi + flèche) au-dessus du sprite. */
  private showSurvivorBubble(p: PlacedSurvivor, message: string): void {
    // Détruit la précédente bulle si encore active.
    if (p.bubble) {
      p.bubble.destroy();
      p.bubble = null;
    }
    // Cache le badge de niveau pendant l'affichage de la bulle (évite la
    // collision visuelle nom doré / étoiles dorées).
    p.levelBadge?.setVisible(false);

    const padX = 8, padY = 6;

    // 1. Texte nom (ligne doré) + texte message (ligne blanche).
    const nameText = this.add.text(0, 0, p.data.name, {
      fontSize: '10px',
      color: '#f1c40f',
      fontFamily: FONT_BODY,
      fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    const messageText = this.add.text(0, 0, message, {
      fontSize: '10px',
      color: '#ecf0f1',
      fontFamily: FONT_BODY,
      align: 'center',
      wordWrap: { width: 180 },
    }).setOrigin(0.5, 0);

    // 2. Calcul dimensions une seule fois.
    const w = Math.max(nameText.width, messageText.width) + padX * 2;
    const h = nameText.height + messageText.height + padY * 2 + 2;

    // 3. Positionne les textes dans la bulle (origine au centre du conteneur).
    nameText.y = -h / 2 + padY;
    messageText.y = nameText.y + nameText.height + 2;

    // 4. Dessine le cadre + flèche vers le sprite.
    const bg = this.add.graphics();
    bg.fillStyle(0x1a1a2e, 0.92);
    bg.lineStyle(1, 0xf1c40f, 0.85);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 6);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 6);
    bg.fillStyle(0x1a1a2e, 0.92);
    bg.fillTriangle(-4, h / 2 - 1, 4, h / 2 - 1, 0, h / 2 + 5);
    bg.lineStyle(1, 0xf1c40f, 0.85);
    bg.lineBetween(-4, h / 2 - 1, 0, h / 2 + 5);
    bg.lineBetween(0, h / 2 + 5, 4, h / 2 - 1);

    const bubble = this.add.container(
      p.sprite.x,
      p.sprite.y - IslandTileRenderer.SURVIVOR_SIZE - 8,
      [bg, nameText, messageText],
    );
    bubble.setDepth(50);
    p.bubble = bubble;

    // Fade-out + destroy après BUBBLE_DURATION.
    this.tweens.add({
      targets: bubble,
      alpha: { from: 1, to: 0 },
      duration: 500,
      delay: BUBBLE_DURATION,
      onComplete: () => {
        bubble.destroy();
        if (p.bubble === bubble) p.bubble = null;
        // Restaure le badge de niveau (caché pendant la bulle).
        p.levelBadge?.setVisible(true);
      },
    });
  }

  /** Petit "✖" rouge qui fade sur les tiles non marchables (feedback visuel) */
  private flashUnwalkable(wx: number, wy: number): void {
    const mark = this.add.text(wx, wy, '✖', {
      fontSize: '16px', color: '#e74c3c', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(8);
    this.tweens.add({
      targets: mark,
      alpha: 0,
      y: wy - 12,
      duration: 500,
      onComplete: () => mark.destroy(),
    });
  }

  shutdown(): void {
    this.captainMoveTween?.stop();
    this.cancelCaptainAnim();
    // Sprint A3 — nettoie tween + timers + bulles + badges niveau des survivants.
    for (const p of this.survivors) {
      p.moveTween?.stop();
      p.animEvent?.remove(false);
      p.nextStepEvent?.remove(false);
      p.bubble?.destroy();
      p.levelBadge?.destroy();
    }
    this.survivors = [];
    // Sprint A4 — arrête l'animation d'eau, les mouettes et les particules.
    this.waterToggleEvent?.remove(false);
    this.waterToggleEvent = null;
    this.waterImages = [];
    this.seagullFrameEvent?.remove(false);
    this.seagullFrameEvent = null;
    this.seagulls = [];
    for (const e of this.campfireEmitters) e.destroy();
    this.campfireEmitters = [];
    this.timeOfDayOverlay?.destroy();
    this.timeOfDayOverlay = null;
  }
}

// ============================================================
// HELPER — HASH DE PATROUILLE (Sprint A3)
// ============================================================

/** Hash déterministe (survivorId, step) → entier ≥ 0. Utilisé pour les pauses
 *  et offsets de patrouille reproductibles. */
function patrolHash(survivorId: string, step: number): number {
  let h = (step * 2654435761) | 0;
  for (let i = 0; i < survivorId.length; i++) {
    h = ((h << 5) - h + survivorId.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// ============================================================
// HELPERS DETERMINISTIC HASH (pour rochers reproductibles)
// ============================================================

/** Hash 2D rapide, déterministe, sans Math.random. */
function hash2(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = (h ^ (h >>> 13)) * 1274126177 | 0;
  return (h ^ (h >>> 16)) >>> 0;
}
